import React from 'react';
import { Play, Sparkles } from 'lucide-react';

interface SpinCenterButtonProps {
  onSpin: () => void;
  isSpinning: boolean;
  disabled: boolean;
  disabledReason?: string;
}

export const SpinCenterButton: React.FC<SpinCenterButtonProps> = ({
  onSpin,
  isSpinning,
  disabled,
  disabledReason,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '100%', maxWidth: '100%' }}>
      <button
        onClick={onSpin}
        disabled={disabled || isSpinning}
        aria-label="Spin the wheel"
        style={{
          position: 'relative',
          padding: '0.85rem clamp(1.25rem, 5vw, 3rem)',
          fontSize: 'clamp(1.05rem, 4vw, 1.35rem)',
          fontWeight: 800,
          fontFamily: 'var(--font-display)',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: '#ffffff',
          background: isSpinning
            ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
            : 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
          borderRadius: '999px',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          boxShadow: isSpinning
            ? '0 0 35px rgba(236, 72, 153, 0.7), inset 0 2px 4px rgba(255,255,255,0.4)'
            : '0 10px 25px rgba(99, 102, 241, 0.5), 0 0 20px rgba(168, 85, 247, 0.4), inset 0 2px 4px rgba(255,255,255,0.4)',
          transform: isSpinning ? 'scale(0.98)' : 'scale(1)',
          cursor: disabled || isSpinning ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.65rem',
          maxWidth: '100%',
          whiteSpace: 'nowrap',
          transition: 'all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isSpinning) {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1.04)';
            e.currentTarget.style.boxShadow =
              '0 15px 35px rgba(99, 102, 241, 0.65), 0 0 30px rgba(236, 72, 153, 0.5), inset 0 2px 6px rgba(255,255,255,0.6)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSpinning) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow =
              '0 10px 25px rgba(99, 102, 241, 0.5), 0 0 20px rgba(168, 85, 247, 0.4), inset 0 2px 4px rgba(255,255,255,0.4)';
          }
        }}
      >
        {isSpinning ? (
          <>
            <Sparkles size={22} className="animate-spin" style={{ animationDuration: '2s', flexShrink: 0 }} />
            <span>Spinning...</span>
          </>
        ) : (
          <>
            <Play size={22} fill="#ffffff" style={{ flexShrink: 0 }} />
            <span>SPIN WHEEL</span>
          </>
        )}
      </button>

      {disabled && disabledReason && (
        <span
          style={{
            fontSize: '0.82rem',
            color: 'var(--text-muted)',
            fontWeight: 500,
            textAlign: 'center',
            padding: '0 0.5rem',
          }}
        >
          {disabledReason}
        </span>
      )}

      {!disabled && !isSpinning && (
        <span
          style={{
            fontSize: '0.8rem',
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            textAlign: 'center',
          }}
          className="desktop-desc"
        >
          Press <kbd style={{ padding: '2px 6px', background: 'var(--bg-surface-elevated)', borderRadius: '4px', border: '1px solid var(--border-subtle)', fontSize: '0.75rem' }}>Space</kbd> to spin
        </span>
      )}
    </div>
  );
};
